import type { MeResponse, PermissionKey } from '@familywallet/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, type ReactNode, useContext, useEffect, useState } from 'react';
import { api, onAuthChange } from './api';

/** endedInApp: was signed in during this visit, then signed out (by the user or the server). */
type AuthState = 'restoring' | 'signedIn' | 'signedOut' | 'endedInApp';

const AuthContext = createContext<AuthState>('restoring');

/** Restores the session from the refresh cookie on load and tracks sign-in state. */
export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [state, setState] = useState<AuthState>(() =>
    api.isSignedIn() ? 'signedIn' : 'restoring',
  );

  useEffect(() => {
    const unsubscribe = onAuthChange((signedIn) => {
      setState((previous) =>
        signedIn ? 'signedIn' : previous === 'signedIn' ? 'endedInApp' : 'signedOut',
      );
      // Never show one person's data to the next person on a shared phone.
      if (!signedIn) queryClient.clear();
    });
    if (!api.isSignedIn()) {
      void api.restoreSession().then((ok) => setState(ok ? 'signedIn' : 'signedOut'));
    }
    return unsubscribe;
  }, [queryClient]);

  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>;
}

export function useAuthState(): AuthState {
  return useContext(AuthContext);
}

export const meQueryKey = ['me'] as const;

/** The signed-in person, their family and permissions (only used inside signed-in screens). */
export function useMe() {
  const signedIn = useAuthState() === 'signedIn';
  return useQuery({ queryKey: meQueryKey, queryFn: () => api.me.get(), enabled: signedIn });
}

/** Whether the signed-in member has a permission. The API checks again on every request. */
export function useCan(permission: PermissionKey): boolean {
  const me = useMe();
  return me.data?.permissions.includes(permission) ?? false;
}

export type Me = MeResponse;
